import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/api';
import { useCompose } from '../lib/compose';
import { KindTabs, type ComposeKind } from './KindTabs';
import { MarkdownEditor } from './MarkdownEditor';
import { MediaUpload, type UploadResult } from './MediaUpload';
import { PostCard } from './PostCard';
import type { Post, Community, Group, Tag } from '../types/api';

type Destination =
    | { kind: 'me' }
    | { kind: 'community'; id: string; slug: string; name: string }
    | { kind: 'group';     id: string; slug: string; name: string };

interface Draft {
    kind:        ComposeKind;
    destination: string;
    title:       string;
    body:        string;
    anon:        boolean;
    startsAt:    string;   // <input type="datetime-local"> value
    endsAt:      string;
    validUntil:  string;
    price:       string;
}

const EMPTY: Draft = {
    kind: 'update', destination: 'me',
    title: '', body: '', anon: false,
    startsAt: '', endsAt: '', validUntil: '', price: '',
};

export function ComposeModal() {
    const { open, closeCompose } = useCompose();
    const { kc } = useAuth();
    const api = useApi();

    const [draft,        setDraft]        = useState<Draft>(EMPTY);
    const [destinations, setDestinations] = useState<Destination[]>([{ kind: 'me' }]);
    const [status,       setStatus]       = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);
    const [submitting,   setSubmitting]   = useState(false);
    const [media,        setMedia]        = useState<UploadResult | null>(null);

    // Fetch destinations once, on first open.
    useEffect(() => {
        if (!open || destinations.length > 1) return;
        (async () => {
            const [c, g] = await Promise.all([
                api.get<{ communities: Community[] }>('/api/communities').catch(() => ({ communities: [] })),
                api.get<{ groups:      Group[]     }>('/api/groups?mine=1').catch(() => ({ groups: [] })),
            ]);
            const list: Destination[] = [{ kind: 'me' }];
            (c.communities ?? []).forEach(cc => list.push({ kind: 'community', id: cc.id, slug: cc.slug, name: cc.name }));
            (g.groups ?? [])     .forEach(gg => list.push({ kind: 'group',     id: gg.id, slug: gg.slug, name: gg.name }));
            setDestinations(list);
        })();
    }, [open, api, destinations.length]);

    // Reset on close so re-opening is a clean slate.
    useEffect(() => {
        if (!open) { setDraft(EMPTY); setStatus(null); setMedia(null); }
    }, [open]);

    // Escape to close.
    useEffect(() => {
        if (!open) return;
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') closeCompose(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, closeCompose]);

    // Build the preview Post from the current draft. Kept as a memo so the
    // <PostCard /> only re-renders when a relevant field changes.
    const previewPost = useMemo<Post>(() => {
        const claims = (kc?.tokenParsed ?? {}) as Record<string, string>;
        const displayName = draft.anon
            ? 'Anon in Vredehoek #????'
            : [claims.given_name, claims.family_name].filter(Boolean).join(' ')
              || claims.preferred_username || claims.email || 'You';
        return {
            id: 'preview',
            posted_as: 'user',
            post_type: draft.kind,
            title: draft.title || null,
            body: draft.body,
            media_url: media?.thumbnail_url || media?.url || null,
            is_anonymous: draft.anon,
            created_at: new Date().toISOString(),
            tag_ids: [],
            display_identity: displayName,
            starts_at:   draft.kind === 'event' && draft.startsAt   ? new Date(draft.startsAt).toISOString()   : null,
            ends_at:     draft.kind === 'event' && draft.endsAt     ? new Date(draft.endsAt).toISOString()     : null,
            valid_until: draft.kind === 'offer' && draft.validUntil ? new Date(draft.validUntil).toISOString() : null,
            price_text:  draft.kind === 'offer' && draft.price      ? draft.price                              : null,
            like_count: 0,
        };
    }, [draft, kc?.token, media]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Publishing…' });
        setSubmitting(true);

        if (draft.kind === 'event' && !draft.startsAt) {
            setStatus({ tone: 'err', msg: 'Events need a start time.' });
            setSubmitting(false);
            return;
        }

        // Every post picks up the Vredehoek tag for POC — real community-post
        // linking lands with PH0-BE-06 phase 2.
        let vredehoekTag: string | null = null;
        try {
            const tag = await api.get<Tag>('/api/tags/vredehoek');
            vredehoekTag = tag.id;
        } catch { /* fall through — API will accept an empty tag list */ }

        let postedAs: 'user' | 'group' = 'user';
        let postedAsGroupID: string | null = null;
        if (draft.destination.startsWith('group:')) {
            postedAs = 'group';
            postedAsGroupID = draft.destination.slice('group:'.length);
        }

        const toISO = (v: string) => v ? new Date(v).toISOString() : null;
        const body = {
            posted_as: postedAs,
            posted_as_group_id: postedAsGroupID,
            post_type: draft.kind,
            title: draft.title || null,
            body: draft.body,
            media_url: media?.url ?? null,
            is_anonymous: draft.anon,
            tag_ids: vredehoekTag ? [vredehoekTag] : [],
            starts_at:   draft.kind === 'event' ? toISO(draft.startsAt)   : null,
            ends_at:     draft.kind === 'event' ? toISO(draft.endsAt)     : null,
            valid_until: draft.kind === 'offer' ? toISO(draft.validUntil) : null,
            price_text:  draft.kind === 'offer' ? (draft.price || null)   : null,
        };
        try {
            const created = await api.post<Post>('/api/posts', body);
            const dest = destinations.find(d =>
                (d.kind === 'me' && draft.destination === 'me')
                || (d.kind === 'community' && `community:${d.id}` === draft.destination)
                || (d.kind === 'group'     && `group:${d.id}`     === draft.destination)
            );
            // Community destinations submit through the moderation surface.
            // The response tells us if it was auto-approved or is pending review.
            let submissionStatus: 'approved' | 'pending' | 'rejected' | null = null;
            if (dest?.kind === 'community' && created?.id) {
                try {
                    const r = await api.post<{ status: 'approved' | 'pending' | 'rejected' }>(
                        '/api/communities/' + dest.slug + '/submit-post',
                        { post_id: created.id }
                    );
                    submissionStatus = r.status;
                } catch { /* post exists on the paper regardless of community wiring */ }
            }
            const label = dest?.kind === 'me' ? 'your profile'
                : dest?.kind === 'community' ? dest.name
                : dest?.kind === 'group'     ? dest.name
                : 'your feed';
            const suffix = submissionStatus === 'pending'  ? ' — waiting for editor review'
                         : submissionStatus === 'rejected' ? ' — community is closed to new posts'
                         : '';
            setStatus({ tone: 'ok', msg: `✓ Published to ${label}${suffix}` });
            setSubmitting(false);
            // Give the user a beat to see the confirmation, then close +
            // notify anyone listening that they should reload their feed.
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('onhere:post-created'));
                closeCompose();
            }, 1600);
        } catch (err) {
            setStatus({ tone: 'err', msg: 'Publish failed: ' + (err as Error).message });
            setSubmitting(false);
        }
    }

    if (!open) return null;

    return (
        <div id="compose-modal">
            <div className="compose-backdrop" onClick={closeCompose} />
            <div className="compose-panel">
                <div className="compose-header">
                    <h2>New post</h2>
                    <button type="button" className="compose-close" aria-label="Close" onClick={closeCompose}>×</button>
                </div>
                <form onSubmit={onSubmit}>
                    <label className="compose-field">
                        <span>Post to</span>
                        <select
                            value={draft.destination}
                            onChange={(e) => setDraft(d => ({ ...d, destination: e.target.value }))}
                            required
                        >
                            <option value="me">My profile</option>
                            {destinations.some(d => d.kind === 'community') && (
                                <optgroup label="Communities">
                                    {destinations.filter(d => d.kind === 'community').map(d =>
                                        <option key={d.id} value={`community:${(d as Extract<Destination, {kind: 'community'}>).id}`}>{d.name}</option>
                                    )}
                                </optgroup>
                            )}
                            {destinations.some(d => d.kind === 'group') && (
                                <optgroup label="Your groups">
                                    {destinations.filter(d => d.kind === 'group').map(d =>
                                        <option key={d.id} value={`group:${(d as Extract<Destination, {kind: 'group'}>).id}`}>{d.name}</option>
                                    )}
                                </optgroup>
                            )}
                        </select>
                        <em className="compose-hint">Choose where this post shows up first.</em>
                    </label>

                    <KindTabs value={draft.kind} onChange={(k) => setDraft(d => ({ ...d, kind: k }))} />

                    <label className="compose-field">
                        <span>Title</span>
                        <input type="text" maxLength={200} placeholder="A short, honest headline"
                               value={draft.title}
                               onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))} />
                    </label>
                    <div className="compose-field">
                        <span>Body</span>
                        <MarkdownEditor
                            value={draft.body}
                            onChange={(v) => setDraft(d => ({ ...d, body: v }))}
                            placeholder="What's happening?"
                            rows={6}
                            required
                        />
                    </div>

                    {draft.kind === 'event' && (
                        <div className="compose-field">
                            <span>Starts</span>
                            <div className="compose-inline-row">
                                <input type="datetime-local"
                                       value={draft.startsAt}
                                       onChange={(e) => setDraft(d => ({ ...d, startsAt: e.target.value }))} />
                                <span className="separator">→</span>
                                <input type="datetime-local" placeholder="Optional end"
                                       value={draft.endsAt}
                                       onChange={(e) => setDraft(d => ({ ...d, endsAt: e.target.value }))} />
                            </div>
                            <em className="compose-hint">Required for events. End is optional.</em>
                        </div>
                    )}

                    {draft.kind === 'offer' && (
                        <div className="compose-field">
                            <div className="compose-inline-row">
                                <label style={{ flex: 1 }}>
                                    <span>Price / deal</span>
                                    <input type="text" maxLength={40} placeholder='e.g. "R55", "2-for-1", "Free"'
                                           value={draft.price}
                                           onChange={(e) => setDraft(d => ({ ...d, price: e.target.value }))} />
                                </label>
                                <label style={{ flex: 1 }}>
                                    <span>Valid until (optional)</span>
                                    <input type="datetime-local"
                                           value={draft.validUntil}
                                           onChange={(e) => setDraft(d => ({ ...d, validUntil: e.target.value }))} />
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="compose-field">
                        <span>Photo or video (optional)</span>
                        <MediaUpload value={media} onChange={setMedia} />
                    </div>

                    <label className="compose-field-inline">
                        <input type="checkbox" checked={draft.anon}
                               onChange={(e) => setDraft(d => ({ ...d, anon: e.target.checked }))} />
                        Post as <em>Anon in Vredehoek</em>
                    </label>

                    <div className="compose-field">
                        <span>Preview</span>
                        <div className={'compose-preview kind-' + draft.kind}>
                            {!draft.title && !draft.body
                                ? <div className="preview-empty">Start typing to see how it'll look…</div>
                                : <PostCard post={previewPost} dense />}
                        </div>
                    </div>

                    <div className="compose-actions">
                        <button type="button" className="plain" onClick={closeCompose}>Cancel</button>
                        <button type="submit" className="primary" disabled={submitting}>Publish</button>
                        {status && (
                            <span style={{
                                marginLeft: 8,
                                color: status.tone === 'err' ? '#8b1919' : status.tone === 'ok' ? 'var(--accent)' : 'var(--muted)',
                            }}>{status.msg}</span>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
